import React, { useMemo } from 'react';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Body } from '@swyftx/aviary/atoms/Typography';

import { DateTime } from 'luxon';
import { DayPickerProps } from 'react-day-picker';

import { SingleDatePicker } from '../SingleDatePicker/SingleDatePicker';

type Props = {
  className?: string;
  dateFrom?: DateTime;
  dateTo?: DateTime;
  setDateFrom: React.Dispatch<React.SetStateAction<DateTime | undefined>>;
  setDateTo: React.Dispatch<React.SetStateAction<DateTime | undefined>>;
} & Omit<DayPickerProps, 'setSelected'>;

const RangeDatePicker: React.FC<Props> = ({ className, dateFrom, dateTo, setDateFrom, setDateTo }) => {
  const selectedDateRange = useMemo(() => {
    if (!dateFrom || !dateTo) return [];

    const dates: Date[] = [];

    const startDate = dateFrom.startOf('day').toJSDate();
    const endDate = dateTo.startOf('day').toJSDate();

    while (startDate <= endDate) {
      dates.push(new Date(startDate)); // Add a copy of the current date
      startDate.setDate(startDate.getDate() + 1); // Increment date by 1 day
    }

    return dates;
  }, [dateFrom, dateTo]);

  return (
    <FlexLayout spacing={16} className={className}>
      <FlexLayout direction='column' spacing={4} className='w-full'>
        <Body size='small' weight='emphasis'>
          From
        </Body>
        <SingleDatePicker
          title='From'
          onChange={setDateFrom}
          toDate={dateTo?.toJSDate()}
          value={dateFrom}
          disabled={{ after: dateTo?.toJSDate() || DateTime.now().endOf('day').toJSDate() }}
          modifiers={{
            sel: selectedDateRange,
            val: [dateTo ? dateTo?.toJSDate() : [], dateFrom ? dateFrom?.toJSDate() : []],
          }}
          modifiersClassNames={{
            sel: '!bg-color-background-neutral text-color-text-primary text-color-text-primary gap-0 !rounded-0 !border-none font-400 w-full',
            val: '!bg-color-text-primary !text-color-text-inverse !rounded-[100%] !w-36 !h-36',
          }}
        />
      </FlexLayout>

      <FlexLayout direction='column' spacing={4} className='w-full'>
        <Body size='small' weight='emphasis'>
          To
        </Body>
        <SingleDatePicker
          title='To'
          onChange={setDateTo}
          value={dateTo}
          fromDate={dateFrom?.toJSDate()}
          disabled={{ before: dateFrom?.toJSDate(), after: DateTime.now().endOf('day').toJSDate() }}
          modifiers={{
            sel: selectedDateRange,
            val: [dateTo ? dateTo?.toJSDate() : [], dateFrom ? dateFrom?.toJSDate() : []],
          }}
          modifiersClassNames={{
            sel: '!bg-color-background-neutral text-color-text-primary text-color-text-primary gap-0 !rounded-0 !border-none font-400 w-full',
            val: '!bg-color-text-primary !text-color-text-inverse !rounded-[100%] !w-36 !h-36',
          }}
        />
      </FlexLayout>
    </FlexLayout>
  );
};

export { RangeDatePicker };
